import { Skeleton } from '@perch-labs/shrike';
import React, { useMemo } from 'react';
import { ReviewBlock } from '~/components/review-block';
import { Text } from '~/components/typography';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { reviewAndViewTermsCopy } from '../review-and-view-terms.copy';

import {
  StyledCrate,
  StyledTwoColumnGrid,
  ProofDocDisplayGrid,
  StyledLink,
  StyledDiv,
} from '../review-and-view-terms.style';

export type ProofDoc = {
  proofDocUrls: (string | null)[];
  proofDocName?: string | null;
  proofDocStatus?: string | null;
  uaLmiSourceTypeId?: number | null;
  utilityAccountId?: number | null;
  utilityAccountNumber?: string | null;
  utilityName?: string | null;
};

export interface ProofDocDisplayProps {
  lmiProofDocs: Array<ProofDoc>;
  funnelPacket: FetchFunnelPacketResponse;
  loading: boolean;
}

type ProofDocEntry = {
  proofDocUrls: {
    front?: string;
    back?: string;
  };
  proofDocName: string;
};

export const ProofDocDisplay = (props: ProofDocDisplayProps) => {
  const { lmiProofDocs, funnelPacket, loading } = props;
  const { proofDocEditabelTab } = reviewAndViewTermsCopy;
  const utilityName = funnelPacket?.utility?.displayName || lmiProofDocs[0]?.utilityName;
  const utilityAccountNumber =
    funnelPacket?.utilityAccount?.accountNumber || lmiProofDocs[0]?.utilityAccountNumber;

  const reviewBlockItems = useMemo(() => {
    const items = {};
    lmiProofDocs.forEach((doc: any) => {
      if (doc.proofDocUrls.length > 0) {
        const key = `${doc.uaLmiSourceTypeId}-${doc.proofDocName}`;
        items[key] = {
          proofDocUrls:
            doc.proofDocUrls.length > 1
              ? { front: doc.proofDocUrls[0], back: doc.proofDocUrls[1] }
              : { front: doc.proofDocUrls[0] },
          proofDocName: doc.proofDocName,
        };
      }
    });
    return items;
  }, [lmiProofDocs]);

  const reviewBlockProofDocArray: ProofDocEntry[] = Object.values(reviewBlockItems);
  const sanitizedUtilityName = utilityAccountNumber
    ? `${utilityName} #${utilityAccountNumber}`
    : utilityName;

  if (loading) {
    return (
      <>
        <Skeleton data-testid="skeleton-component" backgroundColor="tertiary" width="100%" />
        <Skeleton backgroundColor="tertiary" width="100%" />
        <Skeleton backgroundColor="tertiary" width="100%" />
      </>
    );
  } else {
    return utilityName ? (
      <StyledCrate>
        <StyledTwoColumnGrid marginBottom="0">
          <ReviewBlock.Item label={proofDocEditabelTab.title} />
        </StyledTwoColumnGrid>
        <Text paddingBottom={16}>{sanitizedUtilityName}</Text>
        {reviewBlockProofDocArray.length > 0 && (
          <ReviewBlock.Item label="LMI Program" notranslate />
        )}
        {reviewBlockProofDocArray.map((doc, index) => {
          const marginTop = index !== 0 ? '8px' : '0';
          const label = doc
            .proofDocName!.replace(/_/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase())
            .toLocaleUpperCase();
          const hasFrontDoc = Boolean(doc.proofDocUrls.front);
          const hasBackDoc = Boolean(doc.proofDocUrls.back);

          return (
            <React.Fragment key={doc.proofDocName}>
              <StyledTwoColumnGrid marginBottom="0" marginTop={marginTop}>
                <ReviewBlock.Item notranslate>{label}</ReviewBlock.Item>
              </StyledTwoColumnGrid>
              {!!(hasFrontDoc || hasBackDoc) && (
                <ProofDocDisplayGrid aria-label="proof-doc-tab">
                  {['front', 'back'].map(side => {
                    const url = doc.proofDocUrls[side];
                    const cardLabel =
                      proofDocEditabelTab[`card${side.charAt(0).toUpperCase() + side.slice(1)}`];
                    return url && <ReviewBlock.Item key={side} label={cardLabel} />;
                  })}
                </ProofDocDisplayGrid>
              )}

              <ProofDocDisplayGrid aria-label="proof-doc-images">
                {Object.entries(doc.proofDocUrls).map(([side, url]) => {
                  const key = doc.proofDocName + side;
                  return (
                    <StyledLink key={key} href={url ?? ''}>
                      <StyledDiv
                        onClick={e => {
                          e.preventDefault();
                          window.open(url ?? '', '_blank');
                        }}
                        url={url ?? ''}
                      />
                    </StyledLink>
                  );
                })}
              </ProofDocDisplayGrid>
            </React.Fragment>
          );
        })}
      </StyledCrate>
    ) : null;
  }
};
